/* -----------------Globals--------------- */
import { useRef } from "react";

import { defaultAiAssistantInfo } from "@memorang/helpers/src/constants";
/* -----------------Helpers & Hooks--------------- */
import {
	SearchContextProvider,
	useSearchContext,
} from "./contexts/SearchContext";
import { useSearchView } from "./hooks/useSearchView";

/* -----------------Types--------------- */
import type { AiAssistantInfo } from "@memorang/ui/src/components/ChatUI/types";
import type { TextInput } from "react-native";
import type { FeedbackParams } from "../feedback/types";
import type { HandleTracking } from "../types";
import type { VectorStoreParams } from "./types";
import type { OriginalQuestionData } from "./types/question";

import AssistantApplet from "@memorang/ui/src/components/AssistantApplet";
/* -----------------Child components--------------- */
import Box from "@memorang/ui/src/components/Box";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import { TTSContainer } from "../TTS";
type Props = {
	vectorStoreParams: VectorStoreParams;
	context: string;
	defaultQuery?: string;
	feedbackDialogParams?: FeedbackParams;
	handleTracking: HandleTracking;
	userId: string;
	handleClose?: () => void;
	handleNavigateToReference?: (url: string) => void;
	searchContext?: string;
	referenceThumbnails?: Record<string, string>;
	assistantInfo?: AiAssistantInfo;
	userPrompt?: string;
	isItemAnswered?: boolean;
	originalQuestionData?: OriginalQuestionData;
	bottomAppBarStyle?: React.ComponentProps<typeof Box>;
};

export const AiLAMobile = (props: Props) => {
	const { i18n } = useTranslation();
	return (
		<SearchContextProvider
			outputLanguage={i18n.language}
			defaultQuery={props.defaultQuery}
			userId={props.userId}
			vectorStoreParams={props.vectorStoreParams}
			context={props.context}
			sessionId={props.feedbackDialogParams?.sessionId!}
			assistantInfo={props.assistantInfo ?? defaultAiAssistantInfo}
			originalQuestionData={props.originalQuestionData}
			userPrompt={props.userPrompt}
		>
			<InnerAiLAMobile {...props} />
		</SearchContextProvider>
	);
};

const InnerAiLAMobile = ({
	feedbackDialogParams,
	handleTracking,
	handleNavigateToReference,
	searchContext,
	referenceThumbnails,
	isItemAnswered,
	bottomAppBarStyle,
	vectorStoreParams,
}: Props) => {
	const inputRef = useRef<TextInput | null>(null);

	const {
		loading,
		streaming,
		context,
		showReference,
		searchQuery,
		messages,
		abortSearch,
		assistantInfo,
		isThreadLoading,
	} = useSearchContext();

	const {
		handleSelectQuery,
		localRecommendedQueries,
		messageActions,
		appBarFeedbackAction,
		messageFeedbackComponents,
		isRecommendedQuestionsFetching,
		interceptSearchQuery,
		handleResetChatState,
		initialTitle,
		handleShowAllSources,
	} = useSearchView({
		feedbackDialogParams,
		handleTracking,
		searchContext,
	});
	const shouldScrollViewScrollToEnd =
		loading || streaming || isRecommendedQuestionsFetching || isThreadLoading;

	const textToSpeach = (_runId: string, content: string) => (
		<TTSContainer
			itemId={vectorStoreParams.itemId ?? ""}
			explanation={content}
			size={18}
			disabled={streaming}
		/>
	);
	const handleOverlayClose = () => {
		if (router.canGoBack()) {
			router.back();
		}
	};
	return (
		<Box position="relative" height="100%">
			<AssistantApplet
				assistantInfo={assistantInfo}
				messages={messages}
				isRecommendedQuestionsFetching={isRecommendedQuestionsFetching}
				handleShowAllSources={handleShowAllSources}
				loading={loading}
				streaming={streaming}
				localRecommendedQueries={localRecommendedQueries}
				handleSelectQuery={handleSelectQuery}
				context={context}
				showReference={showReference}
				handleNavigateToReference={handleNavigateToReference}
				messageActions={messageActions}
				referenceThumbnails={referenceThumbnails}
				isThreadLoading={isThreadLoading}
				inputQuery={searchQuery.value}
				interceptSearchQuery={interceptSearchQuery}
				helperText={`${assistantInfo.alias} can make mistakes. Check important info.`}
				handleResetChatState={handleResetChatState}
				handleAbortSearch={abortSearch}
				messageFeedbackComponents={messageFeedbackComponents}
				handleOverlayClose={handleOverlayClose}
				isItemAnswered={isItemAnswered}
				initialTitle={initialTitle}
				appBarFeedbackAction={appBarFeedbackAction}
				shouldScrollViewScrollToEnd={shouldScrollViewScrollToEnd}
				textToSpeach={textToSpeach}
				bottomAppBarStyle={bottomAppBarStyle}
				inputRef={inputRef}
				appbarIcon="arrow-left"
			/>
			{messageFeedbackComponents}
		</Box>
	);
};
